import {
  buildEquivalentGroups,
  equivalentMembersSetFor,
  expandOptionsAcrossEquivalentGroups,
} from '../../../supabase/functions/_shared/shoppingCandidates.mjs';

/**
 * Working-set groups — the relationship graph counting and ordering read.
 *
 * Imports no longer merge "same product from another vendor" into one
 * inventory item; they record a CONFIRMED pair in item_relationships instead.
 * An item therefore often owns exactly one purchase option while its real
 * alternatives live on sibling items. This module projects those pairs into
 * connected groups so planning and counting surfaces can treat a group's
 * options as one pool and its count rows as one countable thing.
 *
 * Two rules are load-bearing, both from the relationship domain model
 * (AJ, 2026-08-18):
 *
 *   - Only EQUIVALENT rows group here (relationship_type 'same_item'):
 *     interchangeable purchasing options for one inventory need — limes from
 *     three vendors. Equivalence is transitive, so the union-find closure is
 *     the group. ALTERNATIVE ('purchasing_alternative' — Tito's vs Absolut)
 *     is a real relationship but a product DIFFERENCE the user may care
 *     about: it never pools options, never folds count cards, never orders
 *     on the user's behalf, and is not transitive. same_family is browsing
 *     metadata. Chaining either in is how vodka brands become one count
 *     card no classification ever claimed.
 *   - Only status 'confirmed' rows count. A suggestion nobody reviewed must
 *     never change what gets counted or ordered.
 *
 * Deliberately dependency-free: purchasePlanning imports this, and this
 * imports nothing.
 */

export const WORKING_TYPES = Object.freeze(['same_item']);

/**
 * Connected components over confirmed EQUIVALENT pairs.
 *
 * Rows of any other type or status are ignored, as are rows missing an
 * endpoint or pairing an item with itself. Ids are compared as strings
 * throughout, matching how option.inventory_item_id is compared everywhere
 * else in planning.
 *
 * @param {Array<{item_a_id, item_b_id, relationship_type, status}>} relationships
 * @returns {{ groupIdByItemId: Map<string, string>,
 *             membersByGroupId: Map<string, string[]> }}
 *          groupIdByItemId  item id → its group's id
 *          membersByGroupId group id → member item ids, ascending; the group
 *                           id is the smallest member id as a string
 */
export function buildWorkingSetGroups(relationships) {
  return buildEquivalentGroups(relationships);
}

// The pre-widening name. Same function — the working set used to be
// same_item only, and callers written against that name keep working.
export const buildSameItemGroups = buildWorkingSetGroups;

/**
 * The identity a count line answers to: its group's id when it belongs to a
 * working-set group, otherwise its own id — always a string, null-safe.
 *
 * This is the one key every count-line identity site uses to fold a group
 * into a single countable thing (dedupe, completion accounting, need math).
 */
export function groupIdentityFor(groups, itemId) {
  if (itemId === null || itemId === undefined) return null;
  const key = String(itemId);
  const groupId = groups?.groupIdByItemId?.get?.(key);
  return groupId || key;
}

/**
 * The line's whole group as a Set of item id strings (own id included), or
 * null when the item belongs to no group — the shape isEligibleOption's
 * ctx.groupMembers expects.
 */
export function groupMembersFor(groups, itemId) {
  return equivalentMembersSetFor(groups, itemId);
}

/**
 * A NEW item id → options[] map where every grouped item's pool is the union
 * of its group members' pools.
 *
 * Order is stable and own-first: the item's own options keep their original
 * order, then each sibling's options follow in ascending member-id order.
 * Duplicate option ids keep only their first appearance. Items in no group
 * keep their original pool (same array reference — the map is new, untouched
 * pools are not copied).
 */
export function expandOptionsAcrossGroup(optionsByItemId = {}, groups = null) {
  return expandOptionsAcrossEquivalentGroups(optionsByItemId, groups);
}
